import React, { useState, useCallback } from 'react';
import Papa from 'papaparse';
import { Upload, Download, CheckCircle } from 'lucide-react';
import { logger } from '@/utils/logger';
import { awsAPI } from '@/services/awsAPI';

interface AnalysisResult {
  jobId?: string;
  s3Bucket: string;
  s3Key: string;
  analysis: {
    processableRecords: number;
    invalidRecords: number;
    operation: string;
  };
}

interface BatchUploadSectionProps {
  onFileUploaded: (data: {
    file: File;
    csvPreview: Record<string, string>[];
    totalRows: number;
    processableRows: number;
    flaggedRows: number;
    analysisJobId: string;
    analysisBucket: string;
    analysisKey: string;
    analysisTotalRows: number;
  }) => void;
  onError: (error: Error) => void;
}

export const BatchUploadSection: React.FC<BatchUploadSectionProps> = ({
  onFileUploaded,
  onError
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<Record<string, string>[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [processableRows, setProcessableRows] = useState(0);
  const [flaggedRows, setFlaggedRows] = useState(0);

  const handleFileUpload = useCallback(async (file: File) => {
    logger.info('Starting file upload and analysis', {
      fileName: file.name,
      fileSize: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
      fileType: file.type
    });

    setUploadedFile(file);
    setIsProcessing(true);

    try {
      // First, parse locally for preview (first 10 rows only)
      const previewPromise = new Promise<Record<string, string>[]>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          preview: 10, // Only parse first 10 rows for preview
          complete: (results) => {
            logger.info('Local CSV preview parsing complete', {
              rowsParsed: results.data.length,
              hasHeaders: results.meta.fields?.length || 0
            });
            const validRows = (results.data as Record<string, string>[]).filter((row) =>
              row && typeof row === 'object' && Object.keys(row).some(key => row[key])
            );
            resolve(validRows);
          },
          error: (error) => {
            logger.error('Local CSV preview parsing error:', error);
            reject(error);
          }
        });
      });

      // Parse locally for preview while uploading to AWS
      const [previewData, awsResult] = await Promise.all([
        previewPromise,
        awsAPI.uploadAndAnalyze(file, 'validate')
      ]);

      logger.info('Both local preview and AWS analysis completed', {
        previewRows: previewData.length,
        awsProcessableRows: awsResult.analysis.processableRecords,
        awsInvalidRows: awsResult.analysis.invalidRecords,
        operation: awsResult.analysis.operation,
        jobId: awsResult.jobId,
      });

      // Set preview data from local parsing
      setCsvData(previewData);

      // Set the results from AWS backend analysis for counts
      const totalRowsCount = awsResult.analysis.processableRecords + awsResult.analysis.invalidRecords;
      setTotalRows(totalRowsCount);
      setProcessableRows(awsResult.analysis.processableRecords);
      setFlaggedRows(awsResult.analysis.invalidRecords);
      setIsProcessing(false);

      // Call parent callback with all data
      onFileUploaded({
        file,
        csvPreview: previewData,
        totalRows: totalRowsCount,
        processableRows: awsResult.analysis.processableRecords,
        flaggedRows: awsResult.analysis.invalidRecords,
        analysisJobId: awsResult.jobId || '',
        analysisBucket: awsResult.s3Bucket,
        analysisKey: awsResult.s3Key,
        analysisTotalRows: totalRowsCount,
      });

      // Scroll to preview section
      setTimeout(() => {
        document.getElementById('preview')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);

    } catch (error) {
      logger.error('File analysis failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        fileName: file.name
      });

      setIsProcessing(false);
      onError(error instanceof Error ? error : new Error('File analysis failed'));
    }
  }, [onFileUploaded, onError]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;

    logger.debug('File drop attempted', {
      fileCount: files.length,
      fileType: files.length > 0 ? files[0].type : 'none'
    });

    if (files.length > 0 && files[0].type === 'text/csv') {
      handleFileUpload(files[0]);
    } else if (files.length > 0) {
      logger.warn('Invalid file type dropped', {
        fileName: files[0].name,
        fileType: files[0].type,
        expectedType: 'text/csv'
      });
    }
  }, [handleFileUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    logger.debug('File input selection', {
      fileCount: files?.length || 0,
      fileType: files?.[0]?.type || 'none'
    });

    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload]);

  const downloadSampleCSV = () => {
    logger.info('Downloading CSV template from public assets');
    setIsDownloading(true);

    try {
      // Download the static CSV file from public directory
      // This file is automatically deployed with the app to Amplify
      const link = document.createElement('a');
      link.href = '/collectability-batch-template.csv';
      link.download = 'collectability-batch-template.csv';
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      logger.info('CSV template download initiated', {
        source: 'public_assets',
        fileName: 'collectability-batch-template.csv'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="ca-card ca-card-p flex flex-col justify-between min-h-full">
      <h2 className="h2 mb-8 text-center">Upload Your File</h2>

      {/* File Upload Area */}
      <div
        className="flex-1 flex items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 py-16 px-8 text-center transition-all duration-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer mb-8"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
        />
        <div className="flex flex-col items-center space-y-6 w-full">
          <div className="flex h-16 w-16 items-center justify-center rounded-md border border-slate-300 bg-white">
            {isProcessing ? (
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
            ) : (
              <Upload className="h-8 w-8 text-slate-600" />
            )}
          </div>
          <div>
            <p className="text-lg font-bold text-slate-900 mb-3">
              {uploadedFile
                ? `${uploadedFile.name}`
                : isProcessing ? 'Validating file...' : 'Drag CSV here or browse your files'
              }
            </p>
            {uploadedFile && (
              <p className="text-base font-medium text-slate-900 mb-3">
                {totalRows.toLocaleString()} records detected
              </p>
            )}
            <p className="text-sm text-slate-500">
              CSV required • Up to 50MB • Minimum 10 rows or $0.50 charge • Maximum 5,000 rows
            </p>
            <p className="text-sm text-slate-500 mt-1">
              For more rows{' '}
              <a href="/#contact" className="text-collectability-blue hover:underline">
                contact us
              </a>
            </p>
          </div>
          <button
            onClick={() => document.getElementById('file-upload')?.click()}
            className="ca-btn ca-btn--primary"
          >
            {uploadedFile ? 'Select Different File' : 'Select File'}
          </button>
        </div>
      </div>

      {/* Template Download */}
      <div className="flex justify-center">
        <button
          onClick={downloadSampleCSV}
          className="ca-link flex items-center text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isDownloading}
        >
          {isDownloading ? (
            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          {isDownloading ? 'Downloading...' : 'Download CSV template'}
        </button>
      </div>

      {/* Data Preview - Only shown after upload */}
      {uploadedFile && csvData.length > 0 && (
        <div className="mt-8 pt-8 border-t border-slate-200">
          <div className="mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-success-600" />
            <span className="text-sm font-medium text-success-900">File validation complete</span>
            <span className="text-xs text-slate-500">• {uploadedFile.name} • {(uploadedFile.size / 1024 / 1024).toFixed(2)}MB</span>
          </div>

          {/* Data Preview Table */}
          <div className="mb-4 ca-card ca-card-p">
            <div className="mb-3 flex items-center gap-2">
              <h3 className="h2 text-slate-900">Preview Your Data</h3>
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600">
                Live preview
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="ca-table w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {Object.keys(csvData[0]).slice(0, 6).map((header) => (
                      <th key={header} className="text-left">
                        {header.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {csvData.slice(0, 5).map((row, index) => (
                    <tr key={index} className="ca-row transition-colors">
                      {Object.keys(csvData[0]).slice(0, 6).map((key) => (
                        <td key={key} className="whitespace-nowrap text-sm text-slate-600">
                          {String(row[key] || '').substring(0, 25)}
                          {String(row[key] || '').length > 25 ? '...' : ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs font-medium text-slate-600">
                Showing first 5 of {csvData.length} preview records
              </div>
              <div className="text-xs text-slate-500">
                Refreshes automatically with each upload
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="ca-card ca-card-p text-center">
              <div className="text-[22px] font-semibold text-slate-900">{totalRows.toLocaleString()}</div>
              <div className="text-sm text-slate-600">Total records</div>
            </div>
            <div className="ca-card ca-card-p text-center">
              <div className="text-[22px] font-semibold text-slate-900">{processableRows.toLocaleString()}</div>
              <div className="text-sm text-slate-600">
                Processable ({processableRows > 0 ? Math.round((processableRows / totalRows) * 100) : 0}%)
              </div>
            </div>
            <div className="ca-card ca-card-p text-center">
              <div className="text-[22px] font-semibold text-slate-900">{flaggedRows}</div>
              <div className="text-sm text-slate-600">Need review</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
